import { Icon } from 'Components/shared/Icon';
import { CHAT_GIF_AUTOPLAY_LOOPS } from 'Constants/chatGifDisplay';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '~/lib/utils';
import { useGifEmbedAsset } from '~/modules/ui/gif/hooks/useGifEmbedAsset';
import type {
  GifEmbedPauseReason as PauseReason,
  GifEmbedProps,
} from '~/modules/ui/gif/types';

const CONTROL_BUTTON_CLASSNAME = cn(
  'pointer-events-auto flex h-[60px] shrink-0 cursor-pointer items-center justify-center rounded-[30px] bg-foreground p-[12px] text-background opacity-95 outline-none',
  'focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-foreground'
);

const CONTROL_ICON_CLASSNAME = '!size-9 !min-h-9 !min-w-9 !text-background';

function GifEmbedUnavailable({
  className,
  gifId,
}: {
  className?: string;
  gifId: string;
}) {
  return (
    <div
      role="img"
      aria-label="GIF unavailable"
      data-testid="chat-gif-embed-unavailable"
      data-gif-id={gifId}
      className={cn(
        'box-border flex w-full max-w-[211px] flex-col items-center justify-center gap-2 rounded-[4px] bg-muted px-4 font-sans',
        'aspect-[211/194] min-h-0 shrink-0 text-foreground',
        className
      )}
    >
      <Icon icon="warning" size="large" className="!text-foreground" />

      <p className="m-0 text-center text-[12px] font-normal leading-normal text-foreground">
        GIF unavailable
      </p>
    </div>
  );
}

function VideoControlOverlay({
  visible,
  ariaLabel,
  icon,
  dataTestId,
  onActivate,
}: {
  visible: boolean;
  ariaLabel: string;
  icon: 'play-filled' | 'pause';
  dataTestId: string;
  onActivate: () => void;
}) {
  if (!visible) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
      <button
        type="button"
        data-testid={dataTestId}
        className={CONTROL_BUTTON_CLASSNAME}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          onActivate();
        }}
      >
        <Icon icon={icon} className={CONTROL_ICON_CLASSNAME} />
      </button>
    </div>
  );
}

export const GifEmbed: React.FC<GifEmbedProps> = ({ gifId, className }) => {
  const { status, imageUrl, mp4Url, webmUrl, posterUrl, altText, useVideo } =
    useGifEmbedAsset(gifId);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const loopsLeftRef = useRef(CHAT_GIF_AUTOPLAY_LOOPS);
  const pauseReasonRef = useRef<PauseReason>('none');
  const [loopsLeft, setLoopsLeft] = useState(CHAT_GIF_AUTOPLAY_LOOPS);
  const [pauseReason, setPauseReason] = useState<PauseReason>('none');
  const [hover, setHover] = useState(false);
  const [mediaLoadFailed, setMediaLoadFailed] = useState(false);

  useEffect(() => {
    loopsLeftRef.current = CHAT_GIF_AUTOPLAY_LOOPS;
    pauseReasonRef.current = 'none';
    setLoopsLeft(CHAT_GIF_AUTOPLAY_LOOPS);
    setPauseReason('none');
    setHover(false);
    setMediaLoadFailed(false);
  }, [gifId]);

  const startPlaybackSession = useCallback(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    loopsLeftRef.current = CHAT_GIF_AUTOPLAY_LOOPS;
    pauseReasonRef.current = 'none';
    setLoopsLeft(CHAT_GIF_AUTOPLAY_LOOPS);
    setPauseReason('none');
    video.currentTime = 0;

    void video.play().catch(() => {});
  }, []);

  useLayoutEffect(() => {
    if (!useVideo || status !== 'ready') {
      return;
    }

    const video = videoRef.current;

    if (!video) {
      return;
    }

    startPlaybackSession();
  }, [useVideo, status, gifId, mp4Url, webmUrl, startPlaybackSession]);

  const handleVideoEnded = useCallback(() => {
    if (pauseReasonRef.current !== 'none') {
      return;
    }

    const video = videoRef.current;

    if (!video) {
      return;
    }

    if (loopsLeftRef.current <= 1) {
      loopsLeftRef.current = 0;
      pauseReasonRef.current = 'session';
      setLoopsLeft(0);
      setPauseReason('session');
      return;
    }

    loopsLeftRef.current -= 1;
    setLoopsLeft(loopsLeftRef.current);
    video.currentTime = 0;

    void video.play().catch(() => {});
  }, []);

  const handlePauseClick = useCallback(() => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    video.pause();
    pauseReasonRef.current = 'user';
    setPauseReason('user');
  }, []);

  const handleGifSurfaceClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }

      const video = videoRef.current;

      if (!video) {
        return;
      }

      if (pauseReasonRef.current !== 'none') {
        startPlaybackSession();

        return;
      }

      if (!video.paused) {
        handlePauseClick();
        return;
      }

      void video.play().catch(() => {});
    },
    [handlePauseClick, startPlaybackSession]
  );

  if (status === 'unavailable' || mediaLoadFailed) {
    return <GifEmbedUnavailable gifId={gifId} className={className} />;
  }

  const showLoading = status === 'loading' || (!useVideo && !imageUrl);

  if (showLoading) {
    return (
      <span
        className="text-muted-foreground"
        data-testid="chat-gif-embed-loading"
        data-gif-id={gifId}
      >
        Loading…
      </span>
    );
  }

  if (!useVideo && imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={altText}
        data-testid="chat-gif-embed-image"
        data-gif-id={gifId}
        className={cn(
          'inline-image block rounded-md !max-w-[480px] w-full h-auto max-h-[560px] object-contain',
          className
        )}
        loading="lazy"
        onError={() => {
          setMediaLoadFailed(true);
        }}
      />
    );
  }

  const showPlayOverlay = pauseReason !== 'none';
  const showPauseOverlay = pauseReason === 'none' && hover && loopsLeft > 0;

  return (
    <div
      className={cn(
        'relative inline-block max-w-full cursor-pointer',
        className
      )}
      data-testid="chat-gif-embed"
      data-gif-id={gifId}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onClick={handleGifSurfaceClick}
    >
      <video
        key={gifId}
        ref={videoRef}
        data-testid="chat-gif-embed-video"
        data-gif-id={gifId}
        className={cn(
          'inline-image block rounded-md !max-w-[480px] w-full h-auto max-h-[360px] object-contain'
        )}
        poster={posterUrl ?? undefined}
        muted
        playsInline
        preload="auto"
        aria-label={altText}
        onEnded={handleVideoEnded}
        onError={() => {
          setMediaLoadFailed(true);
        }}
      >
        {webmUrl ? <source src={webmUrl} type="video/webm" /> : null}
        {mp4Url ? <source src={mp4Url} type="video/mp4" /> : null}
      </video>

      <VideoControlOverlay
        visible={showPlayOverlay}
        ariaLabel="Play GIF"
        icon="play-filled"
        dataTestId="chat-gif-embed-play-button"
        onActivate={startPlaybackSession}
      />

      <VideoControlOverlay
        visible={showPauseOverlay}
        ariaLabel="Pause GIF"
        icon="pause"
        dataTestId="chat-gif-embed-pause-button"
        onActivate={handlePauseClick}
      />
    </div>
  );
};
